/* eslint-disable no-underscore-dangle, id-blacklist, no-empty  */
import { CONFIG } from '../config';

export default class BfvImageUpload {
  /**
   * input has custom image
   * static private value
   * @type {boolean}
   */
  hasImage = false;
  /**
   * component has Error
   * @type {boolean}
   */
  hasError = false;
  /**
   * holds the max values
   * we use a map for faster handling
   * @type {Map<string, any>}
   */
  validationSettings = new Map([
    ['maxWidth', undefined],
    ['maxHeight', undefined],
    ['maxSize', undefined],
    ['fileType', undefined],
  ]);
  /**
   * holds the errormessages
   * @type {Map<string, any>}
   */
  errorMessages = new Map([
    ['dimensions', 'Bitte beachten Sie die korrekten Bildabmessungen'],
    ['size', 'Bitte beachten sie die angegebene maximale Größe'],
    ['fileType', 'Bitte beachten Sie die erlaubten Bildtypen'],
  ]);

  constructor(props = {}) {
    this.$root = props.element;
    this.$buttonAdd = this.$root.querySelector('button.js-change-image');
    this.$buttonCancelAdd = this.$root.querySelector('button.js-cancel-upload');
    this.$buttonSave = this.$root.querySelector('button.js-save-image');
    this.$buttonDelete = this.$root.querySelector('button.js-remove-image');
    this.$previewImageWrapper = this.$root.querySelector('.bfv-liveticker-player') || this.$root.querySelector('.bfv-profile-team');
    this.$previewImage = this.$root.querySelector('.js-preview-image img');
    this.$input = this.$root.querySelector('input[type="file"]');
    this.$errorNode = this.$root.querySelector('.input__notification');
    this.$imageSelect = this.$root.querySelector('.js-edit-profile-image-selection');
    this.uploadImgEvent = null;
    this.deleteImgEvent = null;

    this.uploadApiRoute = this.$root.dataset.uploadApi;
    this.deleteApiRoute = this.$root.dataset.deleteApi;
    this.auth = this.$root.dataset.authToken;
    this.additionalHeaders = [];

    /* fn bindings */
    this.__onLoad = this.__onLoad.bind(this);
    this.addFileHandler = this.addFileHandler.bind(this);
    this.saveFileHandler = this.saveFileHandler.bind(this);
    this.resetFileHandler = this.resetFileHandler.bind(this);
    this.deleteFileHandler = this.deleteFileHandler.bind(this);
  }

  /**
   * init class
   */
  async init() {
    if (!this.$root) {
      return;
    }

    window.addEventListener('load', this.__onLoad);

    try {
      this.changeHasImageStatus(...this.__checkStatus());
      this.$root.classList.add('is-loaded');
      this.validationSettings.forEach((value, key) => {
        const dataValue = this.$root.dataset[key];

        if (dataValue) {
          this.validationSettings.set(key, dataValue);
        } else {
          this.validationSettings.delete(key);
        }
      });

      const authHeader = {
        name: 'authorization',
        value: this.auth,
      };

      this.additionalHeaders.push(authHeader);

      this.bindEvents();

    } catch (err) {
      console.error('There was an error initializing the fileInput\n', err); // eslint-disable-line
    }
  }

  // <editor-fold defaultstate="collapsed" desc="*** helper functions ***">
  /**
   * check if has value
   * @private
   */
  __checkStatus() {
    return [this.$root.classList.contains('has-image'), this.$root.classList.contains('has-temporary-image')];
  }

  __setHasImage(shouldHaveImage, shouldHaveTemporaryImage = false) {
    this.hasTemporaryImage = shouldHaveTemporaryImage;
    this.hasImage = shouldHaveImage;

    if (shouldHaveTemporaryImage) {
      this.$root.classList.add('has-temporary-image');
    } else {
      this.$root.classList.remove('has-temporary-image');
    }
  }

  /**
   * toggle between button types
   * @private
   */
  __toggleButtonText() {
    const {
      textChange,
      textAdd,
    } = { ...this.$buttonAdd.dataset };

    const wrapperHasImageClass = `${this.$previewImageWrapper.classList[0]}--has-image`;

    if (this.hasImage) {
      // @formatter:off
      try {
        this.$buttonAdd.querySelector('.btn__text').innerText = textChange;
      } catch (err) {
      }

      // @formatter:on
      this.$previewImageWrapper.classList.add(wrapperHasImageClass);
      this.__showButton(this.$buttonAdd);
      this.__showButton(this.$buttonDelete);
      this.__hideButton(this.$buttonCancelAdd);
      this.__hideButton(this.$buttonSave);
    } else if (this.hasTemporaryImage) {
      this.$previewImageWrapper.classList.add(wrapperHasImageClass);
      this.__hideButton(this.$buttonAdd);
      this.__hideButton(this.$buttonDelete);
      this.__showButton(this.$buttonCancelAdd);
      this.__showButton(this.$buttonSave);
    } else {
      // @formatter:off
      try {
        this.$buttonAdd.querySelector('.btn__text').innerText = textAdd;
      } catch (err) {
      }

      this.$previewImageWrapper.classList.remove(wrapperHasImageClass);
      // @formatter:on
      this.__hideButton(this.$buttonDelete);
      this.__showButton(this.$buttonAdd);
      this.__hideButton(this.$buttonCancelAdd);
      this.__hideButton(this.$buttonSave);
    }
  }

  // noinspection JSMethodCanBeStatic
  /**
   *
   * @param $button
   * @private
   */
  __showButton($button) {
    $button.removeAttribute('disabled');
    $button.classList.remove('hidden');
  }

  // noinspection JSMethodCanBeStatic
  /**
   *
   * @param $button
   * @private
   */
  __hideButton($button) {
    $button.setAttribute('disabled', 'true');
    $button.classList.add('hidden');
  }

  /**
   * wait for window.load to get the images
   * @private
   */
  __onLoad() {
    this.$previewImage = this.$root.querySelector('.js-preview-image img');
  }

  /**
   * select Error Message
   * @param type
   * @returns {*}
   * @private
   */
  __selectError(type) {
    return this.errorMessages.get(type);
  }

  /**
   * put error text in DOM
   * @param msg
   * @private
   */
  __setError(msg = false) {
    if (msg) {
      this.$errorNode.innerText = msg;
      this.$root.classList.add('input--error');
    } else {
      this.$errorNode.innerText = '';
      this.$root.classList.remove('input--error');
    }
  }

  /**
   * renders error messages
   * @param error
   * @private
   */
  __renderError(error) {
    const errorMsg = this.__selectError(error);
    this.__setError(errorMsg);
    this.hasError = !!errorMsg;
  }

  /**
   * check if image x/y fits the max values
   * @param imageBlob
   * @returns {Promise<any>}
   * @private
   */
  __checkImageDimension(imageBlob) {
    return new Promise((resolve, reject) => {
      if (!this.validationSettings.has('maxWidth') && !this.validationSettings.has('maxHeight')) {
        resolve(true); // no max values to check
      }

      const img = new Image();
      img.src = imageBlob;

      img.onload = (ev) => { // eslint-disable-line
        if (this.validationSettings.has('maxWidth') && ev.target.naturalWidth > this.validationSettings.get('maxWidth')) {
          reject('dimensions');
        }

        if (this.validationSettings.has('maxHeight') && ev.target.naturalHeight > this.validationSettings.get('maxHeight')) {
          reject('dimensions');
        }

        resolve(true);
      };
    });
  }

  /**
   * checks if the maxfilesize fits
   * @param file
   * @private
   */
  __checkFileSize(file) {
    if (!this.validationSettings.has('maxSize')) {
      return; // no restriction in size
    }

    if (file.size > this.validationSettings.get('maxSize')) {
      this.__renderError('size');
    }
  }

  /**
   * checks if correct fileType
   * @param file
   * @private
   */
  __checkFileType(file) {
    if (!this.validationSettings.has('fileType')) {
      return; // no restriction in size
    }

    const fileTypes = this.validationSettings.get('fileType')
      .split(',');

    if (!fileTypes.includes(file.type)) {
      this.__renderError('fileType');
    }
  }

  // </editor-fold>

  /**
   * change state of component
   * @param hasImage
   * @param hasTemporaryImage
   */
  changeHasImageStatus(hasImage, hasTemporaryImage = false) {
    this.__setHasImage(hasImage, hasTemporaryImage);

    if (hasImage) {
      this.__toggleButtonText();
      this.$placeholder = this.$root.querySelector('span.icon');

      if (this.$placeholder) {
        this.$placeholder.style.display = 'none';
      }

    } else if (hasTemporaryImage) {
      this.__toggleButtonText();
    } else {
      this.__toggleButtonText();
    }
  }

  /**
   * add preview image src as dataURI to img
   * @param ev
   */
  addFileHandler(ev) {
    this.$previewImage = this.$root.querySelector('.js-preview-image img');
    this.$figure = this.$previewImage.closest('figure');
    this.originalImageSrc = this.$previewImage.src;
    this.$placeholder = this.$root.querySelector('span.icon');

    const target = ev.target;
    const that = this;
    this.__renderError(false); // empty previous error

    if (target.length === 0 || !target.files || target.files.length === 0) {
      return;
    }

    const file = target.files[0];

    this.__checkFileSize(file);
    this.__checkFileType(file);

    // if error stop execution, error handling looks like in GoLang :D
    if (this.hasError) {
      return;
    }

    this.$previewImage.file = file;

    const reader = new FileReader();
    reader.onload = (e) => {
      const imageBlob = e.target.result;

      this.__checkImageDimension(imageBlob)
        .then(() => {
          that.$previewImage.src = imageBlob;
          that.$figure.dataset.imgSrc = imageBlob; // enables galleryLightboxImage resize

          if (that.$placeholder) {
            that.$placeholder.style.display = 'none';
          }

          that.changeHasImageStatus(false, true);
        })
        .catch((error) => {
          this.__renderError(error);
        });
    };

    reader.readAsDataURL(target.files[0]);
  }

  /**
   * reset image input to original image if existing or default image otherwise
   */
  resetFileHandler() {
    this.__renderError(false);
    this.$input.value = '';
    this.changeHasImageStatus(!!this.originalImageSrc, false);

    if (this.originalImageSrc) {
      this.$previewImage.src = this.originalImageSrc;
      this.$placeholder = this.$root.querySelector('span.icon');
      this.$placeholder.style.display = 'none';
    } else {
      this.$previewImage.removeAttribute('src');
      this.$placeholder.style.display = 'block';
    }

    this.originalImageSrc = '';
    this.$previewImage = this.$root.querySelector('.js-preview-image img');
  }

  /**
   * send image
   */
  saveFileHandler() {
    const formData = new FormData();
    const file = this.$input.files[0];
    formData.append('image', file);


    window.divae.restApi
      .sendData(this.uploadApiRoute, formData, null, true, this.additionalHeaders)
      .then(() => {
        this.originalImageSrc = this.$previewImage.src;
        this.changeHasImageStatus(true, false);

        if (this.$imageSelect) {
          this.$root.dispatchEvent(this.uploadImgEvent);
        }
      })
      .catch((e) => {
        console.error('Error uploading new profile image', e);
      });
  }

  /**
   * send delete request
   */
  deleteFileHandler() {
    window.divae.restApi
      .sendData(this.deleteApiRoute, null, 'application/x-www-form-urlencoded', true, this.additionalHeaders)
      .then(() => {
        this.__renderError(false);
        this.$previewImage = this.$root.querySelector('.js-preview-image img');
        this.$previewImage.removeAttribute('src');
        this.originalImageSrc = '';
        this.$placeholder = this.$root.querySelector('span.icon');
        this.$placeholder.style.display = 'block';
        this.changeHasImageStatus(false, false);
        this.$input.value = '';

        if (this.$imageSelect) {
          this.$root.dispatchEvent(this.deleteImgEvent);
        }
      })
      .catch((e) => {
        console.error('Error deleting the profile image', e);
      });
  }

  /**
   * bind custom events
   */
  bindEvents() {
    this.$buttonDelete.addEventListener('click', this.deleteFileHandler, true);
    this.$buttonSave.addEventListener('click', this.saveFileHandler, true);
    this.$input.addEventListener('change', this.addFileHandler, true);
    this.$buttonCancelAdd.addEventListener('click', this.resetFileHandler, true);
    this.$buttonAdd.addEventListener('click', () => this.$input.click(), true);

    if (this.$imageSelect) {
      this.uploadImgEvent = document.createEvent('Event');
      this.uploadImgEvent.initEvent(CONFIG.events.BFV_UPLOADED_IMG, true, true);

      this.deleteImgEvent = document.createEvent('Event');
      this.deleteImgEvent.initEvent(CONFIG.events.BFV_DELETED_IMG, true, true);

      BfvImageUpload.__checkRadioEvents();

    }
  }

  /**
   * check events of radiobuttons if radiogroup exists
   */
  static __checkRadioEvents() {
    this.__setRadios(CONFIG.events.BFV_UPLOADED_IMG, false, true, false);
    this.__setRadios(CONFIG.events.BFV_DELETED_IMG, true, false, true);
  }

  static __setRadios(eventName, disabled, firstChecked, secondChecked) {
    document.addEventListener(eventName, (evt) => {
      const $firstRadio = evt.target.querySelectorAll('.radio')[0].querySelector('input');
      const $secondRadio = evt.target.querySelectorAll('.radio')[1].querySelector('input');

      $secondRadio.disabled = disabled;
      $secondRadio.checked = firstChecked;
      $firstRadio.checked = secondChecked;
    });
  }
}



// WEBPACK FOOTER //
// ./src/javascript/main/BfvImageUpload/index.js